import React, { useEffect } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import InvestorHome from './pages/investor/InvestorHome';
import InvestorKpis from './pages/investor/InvestorKpis';
import InvestorDataRoom from './pages/investor/InvestorDataRoom';
import InvestorNda from './pages/investor/InvestorNda';
import AdminKanban from './pages/admin/AdminKanban';
import AdminCompanies from './pages/admin/AdminCompanies';
import AdminCompanyDetail from './pages/admin/AdminCompanyDetail';
import AdminDataRoom from './pages/admin/AdminDataRoom';
import AdminReports from './pages/admin/AdminReports';
import AdminKpis from './pages/admin/AdminKpis';
import AdminEmails from './pages/admin/AdminEmails';
import QuixInfo from './pages/shared/QuixInfo';
import RetentionMetricsDetail from './pages/shared/RetentionMetricsDetail';
import { ToastProvider } from './components/ToastHost';
import { useInvestorMe } from './hooks/useInvestorMe';

function Topbar({ title, subtitle, children }: { title: string; subtitle?: string; children?: React.ReactNode }) {
  return (
    <div className="topbar">
      <a className="brand" href="/">
        <img className="logo-img" src="/brand/quix-logo.png" alt="Quix" />
        <div>
          <div className="brand-title">{title}</div>
          {subtitle ? <div className="brand-sub">{subtitle}</div> : null}
        </div>
      </a>
      {children}
    </div>
  );
}

function InvestorLayout() {
  const location = useLocation();
  const { me } = useInvestorMe({ enforceNda: false });
  const canSeeDataRoom = (me?.company?.dataRoomAccessMode || 'none') !== 'none';

  useEffect(() => {
    // Basic page-view tracking (best-effort)
    fetch('/api/investor/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ eventType: 'page_view', pathname: location.pathname }),
    }).catch(() => {});
  }, [location.pathname]);

  return (
    <div className="container">
      <Topbar title="Quix Investor Room" subtitle="Secure access to investment materials">
        <div className="nav">
          <NavLink to="/app" end>
            Home
          </NavLink>
          <NavLink to="/app/kpis">Metrics Dashboard</NavLink>
          {canSeeDataRoom ? <NavLink to="/app/data-room">Data Room</NavLink> : null}
        </div>
      </Topbar>
      <div style={{ height: 16 }} />
      <Routes>
        <Route index element={<InvestorHome />} />
        <Route path="nda" element={<InvestorNda />} />
        <Route path="kpis" element={<InvestorKpis />} />
        <Route path="kpis/retention" element={<RetentionMetricsDetail scope="investor" />} />
        <Route path="data-room" element={<InvestorDataRoom />} />
      </Routes>
    </div>
  );
}

function AdminLayout() {
  const location = useLocation();
  return (
    <div className="container">
      <Topbar title="Quix Admin" subtitle="Investor CRM + Data Room">
        <div className="nav">
          <NavLink to="/admin" end>
            Kanban
          </NavLink>
          <NavLink to="/admin/kpis">Metrics Dashboard</NavLink>
          <NavLink to="/admin/quix-info">Quix info</NavLink>
          <NavLink to="/admin/companies">Investors</NavLink>
          <NavLink to="/admin/emails">Emails</NavLink>
          <NavLink to="/admin/data-room">Data Room</NavLink>
          <NavLink to="/admin/reports">Reports</NavLink>
        </div>
      </Topbar>
      <div style={{ height: 16 }} />
      <Routes>
        <Route index element={<AdminKanban />} />
        <Route path="kpis" element={<AdminKpis />} />
        <Route path="kpis/retention" element={<RetentionMetricsDetail scope="admin" />} />
        <Route
	          path="quix-info"
	          element={
	            <div className="grid" style={{ gap: 14 }}>
	              <div className="card qinfo-admin-note">
	                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
	                  <div>
	                    <div style={{ fontSize: 18, fontWeight: 900 }}>Quix info</div>
	                    <div className="muted" style={{ fontSize: 12 }}>
	                      This is the main info page investors see.
	                    </div>
	                  </div>
	                  <a className="btn" href="/app" target="_blank" rel="noreferrer">
	                    Open /app
	                  </a>
	                </div>
	              </div>
	              <QuixInfo />
	            </div>
	          }
	        />
        <Route path="companies" element={<AdminCompanies />} />
        <Route path="companies/:id" element={<AdminCompanyDetail />} />
        <Route path="emails" element={<AdminEmails />} />
        <Route path="data-room" element={<AdminDataRoom />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="*" element={<div className="card">Route not found: {location.pathname}</div>} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <Routes>
        <Route
          path="/"
          element={
            <div className="container">
              <Topbar title="Quix Investor Room" subtitle="Open your personal link to access" />
              <div style={{ height: 16 }} />
              <div className="card">
                <h2 style={{ marginTop: 0 }}>Access with your link</h2>
                <p className="muted" style={{ marginBottom: 0 }}>
                  For security, access is only available through the unique personal link shared with you by the Quix team.
                </p>
              </div>
            </div>
          }
        />
        <Route path="/app/*" element={<InvestorLayout />} />
        <Route path="/admin/*" element={<AdminLayout />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ToastProvider>
  );
}
